// src/Pages/AccountPage.tsx
import React from 'react';
import {
  Stack,
  TextField,
  PrimaryButton,
  DefaultButton,
  Modal,
  Label,
  Text,
  Persona,
  IconButton,
  MessageBar,
  MessageBarType,
  IModalStyles,
} from '@fluentui/react';
import { usePageContext } from '../hooks/PageContext';
import { useParams } from 'react-router-dom';
import { AvatarCropModal } from '../components/modals/AvatarCropModal';
import { InstructionsContainer, H1 } from './Pages-styles';

const AccountPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const ctx = usePageContext();
  const actions = ctx.actions;

  const {
    data,
    loading,       // ✅ Берем настоящий loading из хука
    error,  
    message,
    fileError,
    isModalOpen,
    oldPassword,
    newPassword1,
    newPassword2,
    imageSrc,
    username,
    email,
    phone,
    company,
    setOldPassword,
    setNewPassword1,
    setNewPassword2,
    setIsModalOpen,
    setFileError,
    handleAvatarChange,
    handleSaveAvatar,
    handleSaveProfile,
    handleChangePassword,
    handleSignOut,
    handleEditAvatarClick,
    setIsCropModalOpen,
    adminComment,
    setAdminComment,
  } = actions;

if (loading) return <Text style={{ padding: 20 }}>Загрузка данных...</Text>;
if (error) return <Text style={{ padding: 20, color: 'red' }}>Ошибка загрузки: {error.message}</Text>;
  // 1. Получаем данные целевого пользователя (того, чей профиль открыт)
  const targetUser = id ? data?.userById : data?.me;
  const targetRole = targetUser?.role;
  
  // 2. Если в URL нет ID, значит это точно свой профиль
  const isOwnProfile = !id; 
  
  // 3. Является ли просматриваемый пользователь админом/суперадмином
  const isTargetAdmin = targetRole === 'admin' || targetRole === 'superadmin';

  // 4. Блокируем редактирование, если это чужой профиль И этот пользователь админ/суперадмин
  const isReadOnly = !isOwnProfile && isTargetAdmin;

  // 5. Показываем админскую заметку, только если мы смотрим чужой профиль обычного пользователя
  const showAdminComment = !isOwnProfile && targetRole === 'user';

// 🔍 ОТЛАДКА: посмотрите в консоль браузера (F12)
console.log('🔍 DEBUG AccountPage:', {
  dataId: data?.id,
  userId: actions.user?.id,
  userRole: actions.user?.role,
  targetRole: data?.role,
  isOwnProfile,
  isTargetAdmin,
  isReadOnly,
  showComment: (actions.user?.role === 'admin' || actions.user?.role === 'superadmin') && !isOwnProfile && data?.role === 'user'
});

  const modalStyles: Partial<IModalStyles> = {
    main: {
      maxWidth: 450,
      padding: '30px',
      maxHeight: '90vh',
      overflow: 'auto',
      selectors: {
        '@media (min-width: 480px)': {
          maxWidth: 450,
          width: '100%'
        }
      }
    }
  };

    // 🔹 Форматирование телефона для отображения
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    if (digits.length < 2) return `+${digits}`;
    if (digits.length < 3) return `+${digits[0]} (${digits.slice(1)})`;
    if (digits.length < 6) return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4)}`;
    if (digits.length < 8) return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    if (digits.length < 10) return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  };


  return (
    <InstructionsContainer>
      <Stack tokens={{ childrenGap: 30 }} styles={{ root: { maxWidth: 600, margin: 'auto' } }}>
        <H1>
          {id ? `Редактирование профиля: ${username}` : 'Настройки аккаунта'}
        </H1>
        {message && (
          <MessageBar
            messageBarType={message.type === 'success' ? MessageBarType.success : MessageBarType.error}
            isMultiline={false}
            onDismiss={() => actions.setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}
        {/* Аватар */}
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
          <Persona
            text={username}
            imageUrl={imageSrc || undefined} // ✅ Используем imageSrc
            size={3}
            imageInitials={username.charAt(0).toUpperCase()}
          />
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Изменить аватар"
            onClick={handleEditAvatarClick}
          />
          <input
            type="file"
            accept="image/png, image/jpeg, image/gif"
            hidden
            ref={actions.fileInputRef}
            onChange={handleAvatarChange}
          />
        </Stack>
        {fileError && (
          <MessageBar messageBarType={MessageBarType.error}>
            {fileError}
          </MessageBar>
        )}
        {/* Поля профиля */}
        <TextField
          label="Имя пользователя"
          value={username}
          required
          readOnly={isReadOnly}
          styles={{
            field: isReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#605e5c' } : undefined,
          }}
          onChange={(e, val) => {
            if (!isReadOnly) {
              actions.setMessage(null);
              actions.setUsername(val || '');
            }
          }}
        />
        <TextField
          label="Email"
          value={email}
          required
          readOnly // 🔹 Запрещаем редактирование
          styles={{
            field: { backgroundColor: '#f5f5f5', cursor: 'not-allowed' },
            subComponentStyles: {
              label: {
                root: { color: '#333' }
              }
            }
          }}
          onChange={() => {}} // ❌ На всякий случай, если кто-то попытается изменить
        />
        <TextField
          label="Телефон"
          value={phone}
          required
          readOnly={isReadOnly}
          validateOnLoad={false}
          placeholder="+7 (999) 999-99-99"
          styles={{
            field: isReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#605e5c' } : undefined,
          }}
          onGetErrorMessage={(val) => {
            if (!val || val.trim() === '') return 'Телефон обязателен';
            const raw = val.replace(/\D/g, '');
            if (!(raw.length === 11 && (raw.startsWith('8') || raw.startsWith('7'))) && !(raw.length === 10)) {
              return 'Введите корректный номер телефона';
            }
            return undefined;
          }}
          onChange={(e, val) => {
            if (!isReadOnly) {
              actions.setMessage(null);
              actions.setPhone(formatPhone(val || ''));
            }
          }}
        />
        <TextField
          label="Компания"
          value={company || ''}
          readOnly={isReadOnly}
          styles={{
            field: isReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#605e5c' } : undefined,
          }}
          onChange={(e, val) => {
            if (!isReadOnly) { 
              actions.setMessage(null);
              actions.setCompany(val || '');
            }
          }}
        />
        {/* ✅ Видна ТОЛЬКО если мы смотрим чужой профиль обычного пользователя */}
        {showAdminComment && (
          <TextField
            label="Заметка для администраторов"
            placeholder="Введите дополнительный номер клиента, емаил, настоящее имя или иные сведения..."
            value={adminComment || ''}
            multiline
            rows={4}
            styles={{
              root: {
                marginTop: 10,
                padding: 15,
                backgroundColor: '#f3f2f1',
                borderRadius: 4,
                border: '1px solid #8a8886'
              }
            }}
            onChange={(e, val) => {
              actions.setMessage(null);
              actions.setAdminComment(val || '');
            }}
          />
        )}

        {!isReadOnly && (
          <PrimaryButton text="Сохранить изменения" onClick={handleSaveProfile} />
        )}
        {!id && (
          <>
            <DefaultButton text="Изменить пароль" onClick={() => setIsModalOpen(true)} />
            <DefaultButton text="Выйти" onClick={handleSignOut} />
          </>
        )}
      </Stack>

      {/* Модальное окно для смены пароля */}
      <Modal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        styles={modalStyles}
        titleAriaId="change-password-modal"
      >
        <Stack tokens={{ childrenGap: 15 }}>
          <Text variant="xLarge">Изменить пароль</Text>
          <Label>Старый пароль</Label>
          <TextField
            type="password"
            value={oldPassword}
            onChange={(e, val) => setOldPassword(val || '')}
          />
          <Label>Новый пароль</Label>
          <TextField
            type="password"
            value={newPassword1}
            onChange={(e, val) => setNewPassword1(val || '')}
          />
          <Label>Повторите новый пароль</Label>
          <TextField
            type="password"
            value={newPassword2}
            onChange={(e, val) => setNewPassword2(val || '')}
          />
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton text="Сохранить" onClick={handleChangePassword} />
            <DefaultButton text="Отмена" onClick={() => setIsModalOpen(false)} />
          </Stack>
        </Stack>
      </Modal>

      {/* Модалка аватара — отдельный компонент */}
      <AvatarCropModal
        isOpen={actions.isCropModalOpen}
        src={imageSrc}
        fileError={fileError}
        onClose={() => {
          setIsCropModalOpen(false);
          setFileError(null);
        }}
        onSave={handleSaveAvatar}
      />
    </InstructionsContainer>
  );
};

export default AccountPage;